import { compareArrays } from '../helpers/utility';
import { deleteIn, getIn, insertAt, setIn } from '../helpers/helpers';
import { KeyPath, TemplateEditorPayload, TemplateInsertDirection, TemplateState } from './templateeditor-types';
import { SasToken, Sortable } from 'gf-types';
import { DataLayoutSection, TemplatePage } from '../types/template-types';
import { getParentKeyPath } from './helpers';
import { generateThumbnail } from '../helpers/ImageUtility';
import { uploadFileFromBase64 } from '../helpers/FileUtility';

/** NOTE: This function modifies the given immerjs state object */
export const updateProperty = (draftState: TemplateState, payload: TemplateEditorPayload): TemplateState => {
  for (const keyPath of payload.keyPaths) {
    setIn(draftState, keyPath, payload.value);
  }

  return draftState;
};

/**
 * Reorder items by a given `sortingProperty` and a `startSortNumber` and returns a new reference to the sorted array.
 *
 * @param items The collection to sort.
 * @param sortingProperty The property to sort on.
 * @param startSortNumber The starting number.
 */
export const reorderItems = <T extends Sortable>(items: T[], sortingProperty = 'sortNumber', startSortNumber = 0) => {
  const sortedItems = items
    .sort((a, b) => a[sortingProperty] - b[sortingProperty])
    .filter((item) => !!item)
    .map((item) => ({
      ...item,
      [sortingProperty]: startSortNumber++
    }));

  return sortedItems;
};

export const updateSortOrder = <T extends Sortable>(sortedItems: T[], sortingProperty = 'sortNumber', startSortNumber = 0) => {
  const updatedItems = [...sortedItems];

  for (let i = 0; i < sortedItems.length; i++) {
    updatedItems[i][sortingProperty] = startSortNumber++;
  }

  return updatedItems;
};

export const moveItemsByKeyPath = (
  state: TemplateState,
  newSectionKeyPath: KeyPath,
  sortingProperty: string,
  movedItemKeyPaths: KeyPath[],
  insertNextToId: string,
  insertDirection: TemplateInsertDirection
): TemplateState => {
  let newSectionItems = getIn<DataLayoutSection[]>(state, newSectionKeyPath);
  let newSortingNumber = 0;
  const insertNextToItem = newSectionItems.find((item) => item.id === insertNextToId);

  if (insertNextToItem) {
    newSortingNumber = insertNextToItem[sortingProperty] + (insertDirection === 'up' || insertDirection === 'left' ? -0.1 : 0.1);
  }

  const movedItems = movedItemKeyPaths.map((keyPath) => ({
    sectionItemsKeyPath: getParentKeyPath(keyPath),
    item: getIn<DataLayoutSection>(state, keyPath)
  }));

  movedItems.forEach((movedItem, itemIdx) => {
    if (!compareArrays(movedItem.sectionItemsKeyPath, newSectionKeyPath)) {
      let previousSectionItems = getIn<DataLayoutSection[]>(state, movedItem.sectionItemsKeyPath);
      const movedItemIndex = previousSectionItems.findIndex((x) => x.id === movedItem.item.id);

      previousSectionItems = previousSectionItems.filter((_, idx) => idx !== movedItemIndex);
      setIn(state, movedItem.sectionItemsKeyPath, reorderItems(previousSectionItems, sortingProperty));
      newSectionItems = insertAt(newSectionItems, movedItemIndex, movedItem.item);
    }

    const newMovedItemIndex = newSectionItems.findIndex((x) => x.id === movedItem.item.id);

    setIn(newSectionItems, [newMovedItemIndex, sortingProperty], newSortingNumber + itemIdx / 1000000);
  });

  setIn(state, newSectionKeyPath, reorderItems(newSectionItems, sortingProperty));
  // TODO eventually try to keep the selection
  state.selection = null;

  return state;
};

export const moveItem = (
  state: TemplateState,
  previousSectionKeyPath: KeyPath,
  newSectionKeyPath: KeyPath,
  sortingProperty: string,
  movedItemId: string,
  insertNextToId: string,
  insertDirection: TemplateInsertDirection
) => {
  let previousSectionItems = getIn<TypeMePlease[]>(state, previousSectionKeyPath);
  let newSectionItems = getIn<TypeMePlease[]>(state, newSectionKeyPath);

  let newSortingNumber = 0;
  const insertNextToItem = newSectionItems.find((item) => item.id === insertNextToId);

  if (insertNextToItem) {
    newSortingNumber = insertNextToItem[sortingProperty] + (insertDirection === 'up' || insertDirection === 'left' ? -0.1 : 0.1);
  }

  let movedItemIndex = previousSectionItems.findIndex((item) => item.id === movedItemId);

  if (previousSectionItems !== newSectionItems) {
    const movedItem = getIn<TypeMePlease>(previousSectionItems, [movedItemIndex]);
    previousSectionItems = previousSectionItems.filter((_, idx) => movedItemIndex !== idx);
    setIn(state, previousSectionKeyPath, reorderItems(previousSectionItems, sortingProperty));

    movedItemIndex = newSectionItems.length;
    newSectionItems = insertAt(newSectionItems, movedItemIndex, movedItem);
  }

  setIn(newSectionItems, [movedItemIndex, sortingProperty], newSortingNumber);
  setIn(state, newSectionKeyPath, reorderItems(newSectionItems, sortingProperty));
  // TODO eventually try to keep the selection
  state.selection = null;

  return state;
};

export const deleteItem = (state: TemplateState, keyPathToDelete: KeyPath): TemplateState => {
  const parentListKeyPath: KeyPath = [...keyPathToDelete];
  const itemIndex = parentListKeyPath.pop();
  const items = getIn<unknown[]>(state, parentListKeyPath);

  deleteIn(items, [itemIndex]);

  const reorderedItems = reorderItems(items, 'sortNumber');

  setIn(state, parentListKeyPath, reorderedItems);

  state.selection = null;

  return state;
};

/**
 * Creates and uploads a thumbnail for pages with elements on them.
 *
 * @param sasToken The sas token to use for the thumbnail.
 * @param pages The pages to create the thumbnail from.
 */
export const createPageElementThumbnail = async (sasToken: SasToken, pages: TemplatePage[]): Promise<string | null> => {
  if (!pages || pages.length === 0) {
    return null;
  }

  const pageFileId = pages[0].fileId || pages[0].backgroundFileId;
  const firstPageUrl = `${sasToken.baseUrl}${pageFileId}${sasToken.token}`;
  const thumbnailImage = await generateThumbnail(firstPageUrl);
  const fileUploadResult = await uploadFileFromBase64(thumbnailImage.dataUri, thumbnailImage.width, thumbnailImage.height, false, true);
  const newThumbnailId = fileUploadResult.fileId;

  return newThumbnailId;
};
